import axios from "axios";
import { trackEvent, sanitizeUrlForAnalytics } from "./umami";

function resolveRequestUrl(config) {
  if (!config) return "";
  try {
    const path = config.url || "";
    if (path.startsWith("http")) return sanitizeUrlForAnalytics(path);
    const base = (config.baseURL || "").replace(/\/$/, "");
    const p = path.replace(/^\//, "");
    const full = base ? `${base}/${p}` : path || "";
    return sanitizeUrlForAnalytics(full);
  } catch {
    return sanitizeUrlForAnalytics(String(config.url || ""));
  }
}

function attachUmamiInterceptors(instance) {
  if (!instance || instance.__umamiAnalyticsAttached) return;
  Object.defineProperty(instance, "__umamiAnalyticsAttached", {
    value: true,
    enumerable: false,
    configurable: false,
  });

  instance.interceptors.request.use(
    (config) => {
      const start =
        typeof performance !== "undefined" ? performance.now() : Date.now();
      config._umamiStart = start;
      return config;
    },
    (error) => Promise.reject(error),
  );

  instance.interceptors.response.use(
    (response) => {
      const cfg = response.config || {};
      const start = cfg._umamiStart;
      const end =
        typeof performance !== "undefined" ? performance.now() : Date.now();
      const ms = start != null ? Math.round(end - start) : null;
      trackEvent("api.call", {
        url: resolveRequestUrl(cfg),
        method: (cfg.method || "get").toUpperCase(),
        status: response.status,
        response_time_ms: ms,
      });
      return response;
    },
    (error) => {
      const cfg = error.config || {};
      const start = cfg._umamiStart;
      const end =
        typeof performance !== "undefined" ? performance.now() : Date.now();
      const ms = start != null ? Math.round(end - start) : null;
      const status = error.response?.status ?? null;
      const fields = {
        url: resolveRequestUrl(cfg),
        method: (cfg.method || "get").toUpperCase(),
        status,
        response_time_ms: ms,
      };
      trackEvent("api.call", fields);
      trackEvent("api.error", fields);
      return Promise.reject(error);
    },
  );
}

let installed = false;

/**
 * Must run before any axios.create() in the app (import first in index.js).
 */
export function installAxiosAnalytics() {
  if (typeof window === "undefined" || installed) return;
  installed = true;

  attachUmamiInterceptors(axios);

  const originalCreate = axios.create.bind(axios);
  axios.create = function createWithAnalytics(config) {
    const instance = originalCreate(config);
    attachUmamiInterceptors(instance);
    return instance;
  };
}
